import { FunctionComponent, useState } from 'react'

import { Grid } from '@material-ui/core'
import { useAuth } from 'modules/auth/hooks/auth'

import ITransferenciaContaBancariaRequestDTO from 'modules/pagamentos/domain/entities/ITransferenciaContaBancariaRequestDTO'
import ITransferenciaTmovPayRequestDTO from 'modules/pagamentos/domain/entities/ITransferenciaTmovPayRequestDTO'
import { IComprovanteTransferencia } from 'modules/pagamentos/domain/usecases/ComprovanteTransferencia'
import { ITransferenciaContaBancaria } from 'modules/pagamentos/domain/usecases/TransferenciaContaBancaria'
import { ITransferenciaTmovPay } from 'modules/pagamentos/domain/usecases/TransferenciaTmovPay'
import { useTransferencia } from 'modules/pagamentos/hooks/useTransferencia'
import { useFrotista } from 'modules/user/hooks/useFrotista'
import ConfirmaTransferencia from 'shared/components/ConfirmaTransferencia'
import LabelValue from 'shared/components/LabelValue/LabelValue'
import LoadingBackdrop from 'shared/components/LoadingBackdrop'
import SnackBar from 'shared/components/SnackBar'
import { colors } from 'shared/styles/colors'
import { encrypt } from 'shared/utils/crypt'
import { cpfCnpjMask, numberToCurrency } from 'shared/utils/formatters'
import { registerEvent } from 'shared/services/TrackingGAService'

import ModalTransferencia from '../../../ModalTransferencia'
import LabelValueInfo from './LabelValueInfo'
import { IEnviarPinTmovPay } from 'modules/pagamentos/domain/usecases/EnviarPinTmovPay'
import { IValidarPinTmovPay } from 'modules/pagamentos/domain/usecases/ValidarPinTmovPay'
import Pin from '../../../Pin'
import ModalSenha from '../../../ModalSenha'
import { useCartao } from 'modules/pagamentos/hooks/useCartao'
import { IVerifyPassword } from 'modules/pagamentos/domain/usecases/VerifyPassword'
import { EOrigemSolicitacaoPin } from 'modules/pagamentos/domain/entities/EOrigemEnvioPin'

type TProps = {
  transferenciaTmovPay: ITransferenciaTmovPay
  transferenciaContaBancaria: ITransferenciaContaBancaria
  comprovanteTransferencia: IComprovanteTransferencia
  enviarPinTmovPay: IEnviarPinTmovPay
  validarPinTmovPay: IValidarPinTmovPay
  verifyPassword: IVerifyPassword
}

const Confirmacao: FunctionComponent<TProps> = ({
  transferenciaTmovPay,
  transferenciaContaBancaria,
  comprovanteTransferencia,
  enviarPinTmovPay,
  validarPinTmovPay,
  verifyPassword,
}) => {
  const { frotistaId } = useFrotista()
  const { cartao, setCartao } = useCartao()
  const [loading, setLoading] = useState(false)
  const [openErrorMessage, setOpenErrorMessage] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const {
    handleBackButton,
    tipoTransferencia,
    cpf,
    valor,
    nomeFavorecido,
    setSenha,
    contaFavorecido,
    cnpjFavorecido,
    salvarContaFavorecido,
    pin,
    token,
    contaPagamento,
  } = useTransferencia()
  const { name } = useAuth()
  const [openModalSucesso, setOpenModalSucesso] = useState(false)
  const [protocolo, setProtocolo] = useState('')
  const [openModalPin, setOpenModalPin] = useState(false)
  const [openModalSenha, setOpenModalSenha] = useState(false)

  const montaDadosTransferenciaTmov = (password: string) => {
    const dadosTransferencia = {
      documento_destino: cpf,
      senha: password,
      valor: +valor?.toString(),
      pin: pin.current,
      token: token.current,
      tipo_conta: contaPagamento?.tipo_conta,
    }
    return dadosTransferencia
  }

  const montaDadosTransferenciaContaBancaria = (password: string) => {
    const dadosTransferencia = {
      documento_favorecido: cnpjFavorecido,
      conta_bancaria: {
        codigo_bacen_banco: contaFavorecido.codigo_bacen_banco,
        digito_conta: contaFavorecido.digito_conta,
        nome_banco: contaFavorecido.nome_banco,
        nome_conta: contaFavorecido.nome_conta,
        agencia: contaFavorecido.agencia,
        conta: contaFavorecido.conta,
        tipo_conta: contaFavorecido.tipo_conta,
      },
      senha: password,
      valor: +valor?.toString(),
      salvar_conta_favorecido: salvarContaFavorecido,
      pin: pin.current,
      token: token.current,
    }
    return dadosTransferencia
  }

  const executaTransferenciaContaBancaria = async (password: string) => {
    const dados = montaDadosTransferenciaContaBancaria(password)

    const request: ITransferenciaContaBancariaRequestDTO = {
      body: encrypt(dados, false, frotistaId.toString()),
      frotista_id: frotistaId,
    }
    return transferenciaContaBancaria.execute(request)
  }

  const executaTransferenciaTmov = async (password: string) => {
    const dados = montaDadosTransferenciaTmov(password)
    const request: ITransferenciaTmovPayRequestDTO = {
      body: encrypt(dados, false, frotistaId.toString()),
      frotista_id: frotistaId,
    }
    const response = await transferenciaTmovPay.execute(request)
    return response
  }

  const registrarEvento = () => {
    const mensagem =
      tipoTransferencia === 'tmov'
        ? 'para contratado'
        : 'para mesma titularidade'
    registerEvent({
      categoria: 'Pagamentos',
      acao: `Transferência ${mensagem}`,
      local: 'Tela de Pagamentos',
    })
  }

  const handleConfirmarTransferencia = async (password: string) => {
    registrarEvento()
    setLoading(true)
    setSenha(password)

    try {
      if (tipoTransferencia === 'tmov') {
        const response = await executaTransferenciaTmov(password)
        setProtocolo(response.protocolo)
      } else {
        const response = await executaTransferenciaContaBancaria(password)
        setProtocolo(response.protocolo)
      }
      setSenha(null)
      setLoading(false)
      setOpenModalSucesso(true)
    } catch (e: unknown) {
      if (e instanceof Error) {
        setErrorMessage(
          e.message ||
            'Não foi possível realizar a transferência, por favor tente novamente em alguns minutos.',
        )
      } else {
        setErrorMessage(
          'Não foi possível realizar a transferência, por favor tente novamente em alguns minutos.',
        )
      }
      setOpenErrorMessage(true)
      setLoading(false)
    }
  }

  const handleModalPin = () => {
    setOpenModalPin(!openModalPin)
  }

  return (
    <ConfirmaTransferencia
      handleBackButton={handleBackButton}
      handleNextButton={handleModalPin}
    >
      <Grid item xs={3}>
        <LabelValue title="De:" value={name} />
      </Grid>
      <Grid item xs={3}>
        {tipoTransferencia === 'tmov' ? (
          <LabelValue
            title="Para:"
            value={`${nomeFavorecido}
            <br/>CPF/CNPJ: ${cpfCnpjMask(cpf)}`}
          />
        ) : (
          <LabelValueInfo
            title="Para:"
            value={nomeFavorecido}
            info={`${contaFavorecido?.nome_banco} | ${contaFavorecido?.agencia} | ${contaFavorecido?.conta}`}
          />
        )}
      </Grid>
      <Grid item xs={6} />
      <Grid item xs={3}>
        <LabelValue
          title="Tipo de transferência"
          value={
            tipoTransferencia === 'tmov'
              ? 'Para contratado'
              : 'Para mesma titularidade'
          }
        />
      </Grid>
      <Grid item xs={3}>
        <LabelValue
          title="Valor"
          value={numberToCurrency(valor)}
          backgroundColor={`${colors?.green}`}
        />
      </Grid>
      <Grid item xs={6} />
      <SnackBar
        open={openErrorMessage}
        setOpen={setOpenErrorMessage}
        severity="error"
        message={errorMessage}
        horizontal="right"
      />
      {loading && <LoadingBackdrop open={loading} color={colors.green} />}

      {openModalPin && (
        <Pin
          closeModal={handleModalPin}
          isOpened={openModalPin}
          enviarPinTmovPay={enviarPinTmovPay}
          validarPinTmovPay={validarPinTmovPay}
          setOpenModalSenha={setOpenModalSenha}
          origem={
            tipoTransferencia === 'tmov'
              ? EOrigemSolicitacaoPin.FROTISTA_TMOVPAY_TRANSFERENCIA_ENTRE_CONTAS
              : EOrigemSolicitacaoPin.FROTISTA_TMOVPAY_TRANSFERENCIA_TED
          }
        />
      )}

      {openModalSenha && (
        <ModalSenha
          setActiveMenu={() => console.log('menu ativo')}
          card={cartao}
          setCartao={setCartao}
          setOpenModalPassword={setOpenModalSenha}
          openModalPassword={openModalSenha}
          verifyPassword={verifyPassword}
          curvature={true}
          customPasswordValidation={password =>
            handleConfirmarTransferencia(password)
          }
        />
      )}

      {openModalSucesso && (
        <ModalTransferencia
          openModal={openModalSucesso}
          setOpenModal={setOpenModalSucesso}
          comprovanteTransferencia={comprovanteTransferencia}
          cnpjFavorecido={cnpjFavorecido}
          nomeFavorecido={nomeFavorecido}
          protocolo={protocolo}
          valor={valor}
        />
      )}
    </ConfirmaTransferencia>
  )
}

export default Confirmacao
